import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './types.gen';
import { auth } from './auth/firebase';
import { config } from './config';
import { getAppCheckToken } from './app-check';

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
};

// Path prefixes exempt from App Check (must stay in sync with backend app_check.go).
const APPCHECK_EXEMPT_PREFIXES = [
  '/admin/',
  '/api/v1/ranking/leaderboard',
  '/api/v1/theme',
  '/api/v1/app/version/check',
  '/api/v1/assets/manifest',
  '/api/v1/users/username/',
  '/api/v1/collectibles/catalog/public',
  '/api/v1/collectibles/nearby',
  '/api/v1/avatars/catalog/free',
  '/api/v1/hives/featured',
  '/api/v1/hives/leaderboard',
];

const appCheckMiddleware: Middleware = {
  async onRequest({ request }) {
    const url = new URL(request.url);
    if (APPCHECK_EXEMPT_PREFIXES.some((p) => url.pathname.startsWith(p))) {
      return request;
    }
    const token = await getAppCheckToken();
    if (token) {
      request.headers.set('X-Firebase-AppCheck', token);
    }
    return request;
  },
};

// Headers that every backend request should carry regardless of endpoint. This
// is the single source for the product header — the openapi-fetch middleware
// below and the plain-fetch bypass wrappers (tickets/http.ts, admin/admin-fetch.ts)
// both read from here, so a new app or wrapper cannot forget it.
export function getCommonHeaders(): Record<string, string> {
  return { 'X-Client-Product': config.clientProduct };
}

// Always applies the product header — independent of the App Check exempt-prefix
// logic above (product labeling is never conditional).
const clientProductMiddleware: Middleware = {
  onRequest({ request }) {
    for (const [key, value] of Object.entries(getCommonHeaders())) {
      request.headers.set(key, value);
    }
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: config.apiUrl });
api.use(authMiddleware);
api.use(appCheckMiddleware);
api.use(clientProductMiddleware);
